import { a as rem, i as _responsive, n as _getArrayProp, s as EMPTY_RECORD } from "./helpers-bwfx1e-Y.js";
import { a as isHTMLElement, c as isHTMLTextAreaElement, i as isHTMLButtonElement, o as isHTMLInputElement, r as isHTMLAnchorElement, s as isHTMLSelectElement } from "./element-BYgCE_iK.js";
import { t as getTheme_v2 } from "./getTheme_v2-y09KqtEb.js";
import { l as useRootTheme, m as focusRingStyle, p as focusRingBorderStyle, s as Card, v as responsiveRadiusStyle, y as Box } from "./useLayer-BAVfmbNN.js";
import { n as Text } from "./stack-C2HKTF-l.js";
import { t as Button } from "./button-D6Z-yLlP.js";
import { c } from "react/compiler-runtime";
import { isValidElement, useEffect, useImperativeHandle, useRef } from "react";
import { css, styled } from "styled-components";
import { jsx, jsxs } from "react/jsx-runtime";
import { isValidElementType } from "react-is";
import { CloseIcon } from "@sanity/icons/Close";
/**
* @internal
*/
function _raf(fn) {
	let frameId = requestAnimationFrame(fn);
	return () => {
		cancelAnimationFrame(frameId);
	};
}
/**
* @internal
*/
function _raf2(fn) {
	let innerDispose = null, outerDispose = _raf(() => {
		innerDispose = _raf(fn);
	});
	return () => {
		innerDispose && innerDispose(), outerDispose();
	};
}
/**
* @internal
*/
function _hasFocus(element) {
	return !!document.activeElement && element.contains(document.activeElement);
}
/**
* @internal
*/
function isFocusable(element) {
	return element.tabIndex > 0 || element.tabIndex === 0 && element.getAttribute("tabIndex") !== null ? !0 : isHTMLAnchorElement(element) ? !!element.href && element.rel !== "ignore" : isHTMLInputElement(element) ? element.type !== "hidden" && element.type !== "file" && !element.disabled : isHTMLButtonElement(element) || isHTMLSelectElement(element) || isHTMLTextAreaElement(element) ? !element.disabled : !1;
}
/**
* @internal
*/
function attemptFocus(element) {
	if (!isFocusable(element)) return !1;
	try {
		element.focus();
	} catch {}
	return document.activeElement === element;
}
/**
* @internal
*/
function focusFirstDescendant(element) {
	for (let i = 0; i < element.childNodes.length; i++) {
		let child = element.childNodes[i];
		if (isHTMLElement(child) && (attemptFocus(child) || focusFirstDescendant(child))) return !0;
	}
	return !1;
}
/**
* @internal
*/
function focusLastDescendant(element) {
	for (let i = element.childNodes.length - 1; i >= 0; i--) {
		let child = element.childNodes[i];
		if (isHTMLElement(child) && (attemptFocus(child) || focusLastDescendant(child))) return !0;
	}
	return !1;
}
/**
* @beta
*/
function useCustomValidity(ref, customValidity) {
	let $ = c(4), t0, t1;
	$[0] !== customValidity || $[1] !== ref ? (t0 = () => {
		ref.current?.setCustomValidity(customValidity || "");
	}, t1 = [customValidity, ref], $[0] = customValidity, $[1] = ref, $[2] = t0, $[3] = t1) : (t0 = $[2], t1 = $[3]), useEffect(t0, t1);
}
function responsiveInputPaddingStyle(props) {
	let { $fontSize, $iconLeft, $iconRight, $padding, $space } = props, { font, media, space } = getTheme_v2(props.theme), len = Math.max($padding.length, $space.length, $fontSize.length), _padding = [], _space = [], _fontSize = [];
	for (let i = 0; i < len; i += 1) _fontSize[i] = $fontSize[i] === void 0 ? _fontSize[i - 1] : $fontSize[i], _padding[i] = $padding[i] === void 0 ? _padding[i - 1] : $padding[i], _space[i] = $space[i] === void 0 ? _space[i - 1] : $space[i];
	return _responsive(media, _padding, (_, i) => {
		let size = font.text.sizes[_fontSize[i]] || font.text.sizes[2], emSize = size.lineHeight - size.ascenderHeight - size.descenderHeight, p = space[_padding[i]], s = space[_space[i]], styles = {
			paddingTop: rem(p - size.ascenderHeight),
			paddingRight: rem(p),
			paddingBottom: rem(p - size.descenderHeight),
			paddingLeft: rem(p)
		};
		return $iconRight && (styles.paddingRight = rem(p + emSize + s)), $iconLeft && (styles.paddingLeft = rem(p + emSize + s)), styles;
	});
}
function responsiveInputPaddingIconRightStyle(props) {
	return responsiveInputPaddingStyle({
		...props,
		$iconRight: !0
	});
}
const ROOT_STYLE = css`&:not([hidden]){display:flex;}align-items:center;`;
function textInputRootStyle() {
	return ROOT_STYLE;
}
function textInputBaseStyle(props) {
	let { $scheme, $tone, $weight } = props, { color, font } = getTheme_v2(props.theme);
	return css`appearance:none;background:none;border:0;border-radius:0;outline:none;width:100%;box-sizing:border-box;font-family:${font.text.family};font-weight:${$weight && font.text.weights[$weight] || font.text.weights.regular};margin:0;position:relative;z-index:1;display:block;&:-webkit-autofill,&:-webkit-autofill:hover,&:-webkit-autofill:focus,&:-webkit-autofill:active{-webkit-text-fill-color:var(--input-fg-color) !important;transition:background-color 5000s;transition-delay:86400s;}&[data-as='textarea']{resize:none;}color:var(--input-fg-color);&::placeholder{color:var(--input-placeholder-color);}&[data-scheme='${$scheme}'][data-tone='${$tone}']{--input-fg-color:${color.input.default.enabled.fg};--input-placeholder-color:${color.input.default.enabled.placeholder};&:not(:invalid):not(:disabled):not(:read-only){--input-fg-color:${color.input.default.enabled.fg};--input-placeholder-color:${color.input.default.enabled.placeholder};}&:not(:invalid):disabled{--input-fg-color:${color.input.default.disabled.fg};--input-placeholder-color:${color.input.default.disabled.placeholder};}&:invalid{--input-fg-color:${color.input.invalid.enabled.fg};--input-placeholder-color:${color.input.invalid.enabled.placeholder};}&:read-only{--input-fg-color:${color.input.default.readOnly.fg};--input-placeholder-color:${color.input.default.readOnly.placeholder};}}`;
}
function textInputFontSizeStyle(props) {
	let { font, media } = getTheme_v2(props.theme);
	return _responsive(media, props.$fontSize, (sizeIndex) => {
		let size = font.text.sizes[sizeIndex] || font.text.sizes[2];
		return {
			fontSize: rem(size.fontSize),
			lineHeight: `${size.lineHeight / size.fontSize}`
		};
	});
}
function textInputRepresentationStyle(props) {
	let { $hasPrefix, $hasSuffix, $scheme, $tone, $unstableDisableFocusRing } = props, { color, input } = getTheme_v2(props.theme);
	return css`--input-box-shadow:none;position:absolute;top:0;left:0;right:0;bottom:0;display:block;pointer-events:none;z-index:0;background-color:var(--card-bg-color);box-shadow:var(--input-box-shadow);border-top-left-radius:${$hasPrefix ? 0 : void 0};border-bottom-left-radius:${$hasPrefix ? 0 : void 0};border-top-right-radius:${$hasSuffix ? 0 : void 0};border-bottom-right-radius:${$hasSuffix ? 0 : void 0};&[data-scheme='${$scheme}'][data-tone='${$tone}']{--card-bg-color:${color.input.default.enabled.bg};--card-fg-color:${color.input.default.enabled.fg};*:not(:disabled) + &[data-border]{--input-box-shadow:${focusRingBorderStyle({
		color: color.input.default.enabled.border,
		width: input.border.width
	})};}*:not(:disabled):invalid + &{--card-bg-color:${color.input.invalid.enabled.bg};--card-fg-color:${color.input.invalid.enabled.fg};&[data-border]{--input-box-shadow:${focusRingBorderStyle({
		color: color.input.invalid.enabled.border,
		width: input.border.width
	})};}}*:not(:disabled):focus + &{&[data-border]{--input-box-shadow:${$unstableDisableFocusRing ? void 0 : focusRingStyle({
		border: {
			color: color.input.default.enabled.border,
			width: input.border.width
		},
		focusRing: input.text.focusRing
	})};}&:not([data-border]){--input-box-shadow:${$unstableDisableFocusRing ? void 0 : focusRingStyle({ focusRing: input.text.focusRing })};}}*:not(:invalid):disabled + &{--card-bg-color:${color.input.default.disabled.bg} !important;--card-fg-color:${color.input.default.disabled.fg} !important;--card-icon-color:${color.input.default.disabled.fg} !important;&[data-border]{--input-box-shadow:${focusRingBorderStyle({
		color: color.input.default.disabled.border,
		width: input.border.width
	})};}}*:invalid:disabled + &{--card-bg-color:${color.input.invalid.disabled.bg} !important;--card-fg-color:${color.input.invalid.disabled.fg} !important;--card-icon-color:${color.input.invalid.disabled.fg} !important;&[data-border]{--input-box-shadow:${focusRingBorderStyle({
		color: color.input.invalid.disabled.border,
		width: input.border.width
	})};}}*:not(:invalid):read-only + &{--card-bg-color:${color.input.default.readOnly.bg} !important;--card-fg-color:${color.input.default.readOnly.fg} !important;}*:invalid:read-only + &{--card-bg-color:${color.input.invalid.readOnly.bg} !important;--card-fg-color:${color.input.invalid.readOnly.fg} !important;}@media (hover:hover){*:not(:disabled):not(:read-only):not(:invalid):hover + &{--card-bg-color:${color.input.default.hovered.bg};--card-fg-color:${color.input.default.hovered.fg};}*:invalid:not(:disabled):not(:read-only):hover + &{--card-bg-color:${color.input.invalid.hovered.bg};--card-fg-color:${color.input.invalid.hovered.fg};}*:not(:disabled):not(:read-only):not(:invalid):not(:focus):hover + &[data-border]{--input-box-shadow:${focusRingBorderStyle({
		color: color.input.default.hovered.border,
		width: input.border.width
	})};}*:invalid:not(:disabled):not(:read-only):not(:focus):hover + &[data-border]{--input-box-shadow:${focusRingBorderStyle({
		color: color.input.invalid.hovered.border,
		width: input.border.width
	})};}}}`;
}
const CLEAR_BUTTON_BOX_STYLE = { zIndex: 2 }, StyledTextInput = /* @__PURE__ */ styled(Card).attrs({ forwardedAs: "span" }).withConfig({
	displayName: "StyledTextInput",
	componentId: "sc-ceualg-0"
})(textInputRootStyle), InputRoot = styled.span.withConfig({
	displayName: "InputRoot",
	componentId: "sc-ceualg-1"
})`flex:1;min-width:0;display:block;position:relative;`, Prefix = styled(Card).attrs({ forwardedAs: "span" }).withConfig({
	displayName: "Prefix",
	componentId: "sc-ceualg-2"
})`border-top-right-radius:0;border-bottom-right-radius:0;& > span{display:block;margin:-1px;}`, Suffix = styled(Card).attrs({ forwardedAs: "span" }).withConfig({
	displayName: "Suffix",
	componentId: "sc-ceualg-3"
})`border-top-left-radius:0;border-bottom-left-radius:0;& > span{display:block;margin:-1px;}`, Input = /* @__PURE__ */ styled.input.withConfig({
	displayName: "Input",
	componentId: "sc-ceualg-4"
})(responsiveInputPaddingStyle, textInputBaseStyle, textInputFontSizeStyle), Presentation = /* @__PURE__ */ styled.span.withConfig({
	displayName: "Presentation",
	componentId: "sc-ceualg-5"
})(responsiveRadiusStyle, textInputRepresentationStyle), LeftBox = styled(Box).withConfig({
	displayName: "LeftBox",
	componentId: "sc-ceualg-6"
})`position:absolute;top:0;left:0;`, RightBox = styled(Box).withConfig({
	displayName: "RightBox",
	componentId: "sc-ceualg-7"
})`position:absolute;top:0;right:0;`, RightCard = styled(Card).withConfig({
	displayName: "RightCard",
	componentId: "sc-ceualg-8"
})`background-color:transparent;position:absolute;top:0;right:0;`, TextInputClearButton = /* @__PURE__ */ styled(Button).withConfig({
	displayName: "TextInputClearButton",
	componentId: "sc-ceualg-9"
})({ "&:not([hidden])": { display: "block" } });
/**
* Single line text input.
*
* @public
*/
function TextInput(props) {
	let $ = c(93), IconComponent, IconRightComponent, __unstable_disableFocusRing, clearButton, customValidity, forwardedRef, onClear, prefix, readOnly, restProps, suffix, t0, t1, t2, t3, t4, t5, t6, weight;
	$[0] === props ? (IconComponent = $[1], IconRightComponent = $[2], __unstable_disableFocusRing = $[3], clearButton = $[4], customValidity = $[5], forwardedRef = $[6], onClear = $[7], prefix = $[8], readOnly = $[9], restProps = $[10], suffix = $[11], t0 = $[12], t1 = $[13], t2 = $[14], t3 = $[15], t4 = $[16], t5 = $[17], t6 = $[18], weight = $[19]) : ({__unstable_disableFocusRing, border: t0, clearButton, disabled: t1, fontSize: t2, gap: t3, icon: IconComponent, iconRight: IconRightComponent, onClear, padding: t4, prefix, radius: t5, readOnly, ref: forwardedRef, suffix, customValidity, type: t6, weight, ...restProps} = props, $[0] = props, $[1] = IconComponent, $[2] = IconRightComponent, $[3] = __unstable_disableFocusRing, $[4] = clearButton, $[5] = customValidity, $[6] = forwardedRef, $[7] = onClear, $[8] = prefix, $[9] = readOnly, $[10] = restProps, $[11] = suffix, $[12] = t0, $[13] = t1, $[14] = t2, $[15] = t3, $[16] = t4, $[17] = t5, $[18] = t6, $[19] = weight);
	let border = t0 === void 0 || t0, disabled = t1 !== void 0 && t1, fontSizeProp = t2 === void 0 ? 2 : t2, gap = t3 === void 0 ? 3 : t3, paddingProp = t4 === void 0 ? 3 : t4, radiusProp = t5 === void 0 ? 2 : t5, type = t6 === void 0 ? "text" : t6, ref = useRef(null), rootTheme = useRootTheme(), t7;
	$[20] === fontSizeProp ? t7 = $[21] : (t7 = _getArrayProp(fontSizeProp), $[20] = fontSizeProp, $[21] = t7);
	let fontSize = t7, t8;
	$[22] === paddingProp ? t8 = $[23] : (t8 = _getArrayProp(paddingProp), $[22] = paddingProp, $[23] = t8);
	let padding = t8, t9;
	$[24] === radiusProp ? t9 = $[25] : (t9 = _getArrayProp(radiusProp), $[24] = radiusProp, $[25] = t9);
	let radius = t9, t10;
	$[26] === gap ? t10 = $[27] : (t10 = _getArrayProp(gap), $[26] = gap, $[27] = t10);
	let space = t10, $hasClearButton = !!clearButton, $hasIcon = !!IconComponent, $hasIconRight = !!IconRightComponent, $hasSuffix = !!suffix, $hasPrefix = !!prefix, t11;
	$[28] === Symbol.for("react.memo_cache_sentinel") ? (t11 = () => ref.current, $[28] = t11) : t11 = $[28], useImperativeHandle(forwardedRef, t11), useCustomValidity(ref, customValidity);
	let handleClearMouseDown = _temp, t12;
	$[29] === onClear ? t12 = $[30] : (t12 = (event_0) => {
		event_0.preventDefault(), event_0.stopPropagation(), onClear && onClear(), ref.current?.focus();
	}, $[29] = onClear, $[30] = t12);
	let handleClearClick = t12, t13;
	$[31] !== prefix || $[32] !== radius ? (t13 = prefix && /* @__PURE__ */ jsx(Prefix, {
		borderTop: !0,
		borderLeft: !0,
		borderBottom: !0,
		radius,
		sizing: "border",
		tone: "inherit",
		children: /* @__PURE__ */ jsx("span", { children: prefix })
	}), $[31] = prefix, $[32] = radius, $[33] = t13) : t13 = $[33];
	let prefixNode = t13, t14 = border ? "" : void 0, t15;
	$[34] !== IconComponent || $[35] !== fontSize || $[36] !== padding ? (t15 = IconComponent && /* @__PURE__ */ jsx(LeftBox, {
		padding,
		children: /* @__PURE__ */ jsxs(Text, {
			size: fontSize,
			children: [isValidElement(IconComponent) && IconComponent, isValidElementType(IconComponent) && /* @__PURE__ */ jsx(IconComponent, {})]
		})
	}), $[34] = IconComponent, $[35] = fontSize, $[36] = padding, $[37] = t15) : t15 = $[37];
	let t16;
	$[38] !== $hasClearButton || $[39] !== IconRightComponent || $[40] !== fontSize || $[41] !== padding ? (t16 = !$hasClearButton && IconRightComponent && /* @__PURE__ */ jsx(RightBox, {
		padding,
		children: /* @__PURE__ */ jsxs(Text, {
			size: fontSize,
			children: [isValidElement(IconRightComponent) && IconRightComponent, isValidElementType(IconRightComponent) && /* @__PURE__ */ jsx(IconRightComponent, {})]
		})
	}), $[38] = $hasClearButton, $[39] = IconRightComponent, $[40] = fontSize, $[41] = padding, $[42] = t16) : t16 = $[42];
	let t17;
	$[43] !== $hasPrefix || $[44] !== $hasSuffix || $[45] !== __unstable_disableFocusRing || $[46] !== radius || $[47] !== rootTheme.scheme || $[48] !== rootTheme.tone || $[49] !== t14 || $[50] !== t15 || $[51] !== t16 ? (t17 = /* @__PURE__ */ jsxs(Presentation, {
		$hasPrefix,
		$unstableDisableFocusRing: __unstable_disableFocusRing,
		$hasSuffix,
		$radius: radius,
		$scheme: rootTheme.scheme,
		$tone: rootTheme.tone,
		"data-border": t14,
		"data-scheme": rootTheme.scheme,
		"data-tone": rootTheme.tone,
		children: [t15, t16]
	}), $[43] = $hasPrefix, $[44] = $hasSuffix, $[45] = __unstable_disableFocusRing, $[46] = radius, $[47] = rootTheme.scheme, $[48] = rootTheme.tone, $[49] = t14, $[50] = t15, $[51] = t16, $[52] = t17) : t17 = $[52];
	let presentationNode = t17, t18;
	$[53] === padding ? t18 = $[54] : (t18 = padding.map(_temp2), $[53] = padding, $[54] = t18);
	let clearButtonBoxPadding = t18, t19;
	$[55] === padding ? t19 = $[56] : (t19 = padding.map(_temp3), $[55] = padding, $[56] = t19);
	let clearButtonPadding = t19, clearButtonProps = typeof clearButton == "object" ? clearButton : EMPTY_RECORD, t20;
	$[57] !== clearButton || $[58] !== clearButtonBoxPadding || $[59] !== clearButtonPadding || $[60] !== clearButtonProps || $[61] !== customValidity || $[62] !== disabled || $[63] !== fontSize || $[64] !== handleClearClick || $[65] !== radius || $[66] !== readOnly ? (t20 = !disabled && !readOnly && clearButton && /* @__PURE__ */ jsx(RightCard, {
		forwardedAs: "span",
		padding: clearButtonBoxPadding,
		style: CLEAR_BUTTON_BOX_STYLE,
		tone: customValidity ? "critical" : "inherit",
		children: /* @__PURE__ */ jsx(TextInputClearButton, {
			"aria-label": "Clear",
			"data-qa": "clear-button",
			fontSize,
			icon: CloseIcon,
			mode: "bleed",
			padding: clearButtonPadding,
			radius,
			...clearButtonProps,
			onClick: handleClearClick,
			onMouseDown: handleClearMouseDown
		})
	}), $[57] = clearButton, $[58] = clearButtonBoxPadding, $[59] = clearButtonPadding, $[60] = clearButtonProps, $[61] = customValidity, $[62] = disabled, $[63] = fontSize, $[64] = handleClearClick, $[65] = radius, $[66] = readOnly, $[67] = t20) : t20 = $[67];
	let clearButtonNode = t20, t21;
	$[68] !== radius || $[69] !== suffix ? (t21 = suffix && /* @__PURE__ */ jsx(Suffix, {
		borderTop: !0,
		borderRight: !0,
		borderBottom: !0,
		radius,
		sizing: "border",
		tone: "inherit",
		children: /* @__PURE__ */ jsx("span", { children: suffix })
	}), $[68] = radius, $[69] = suffix, $[70] = t21) : t21 = $[70];
	let suffixNode = t21, t22 = $hasIconRight || $hasClearButton, t23;
	$[71] !== $hasIcon || $[72] !== disabled || $[73] !== fontSize || $[74] !== padding || $[75] !== readOnly || $[76] !== restProps || $[77] !== rootTheme.scheme || $[78] !== rootTheme.tone || $[79] !== space || $[80] !== t22 || $[81] !== type || $[82] !== weight ? (t23 = /* @__PURE__ */ jsx(Input, {
		"data-as": "input",
		"data-scheme": rootTheme.scheme,
		"data-tone": rootTheme.tone,
		...restProps,
		$fontSize: fontSize,
		$iconLeft: $hasIcon,
		$iconRight: t22,
		$padding: padding,
		$scheme: rootTheme.scheme,
		$space: space,
		$tone: rootTheme.tone,
		$weight: weight,
		disabled,
		readOnly,
		ref,
		type
	}), $[71] = $hasIcon, $[72] = disabled, $[73] = fontSize, $[74] = padding, $[75] = readOnly, $[76] = restProps, $[77] = rootTheme.scheme, $[78] = rootTheme.tone, $[79] = space, $[80] = t22, $[81] = type, $[82] = weight, $[83] = t23) : t23 = $[83];
	let t24;
	$[84] !== clearButtonNode || $[85] !== presentationNode || $[86] !== t23 ? (t24 = /* @__PURE__ */ jsxs(InputRoot, { children: [
		t23,
		presentationNode,
		clearButtonNode
	] }), $[84] = clearButtonNode, $[85] = presentationNode, $[86] = t23, $[87] = t24) : t24 = $[87];
	let t25;
	return $[88] !== prefixNode || $[89] !== rootTheme.tone || $[90] !== suffixNode || $[91] !== t24 ? (t25 = /* @__PURE__ */ jsxs(StyledTextInput, {
		"data-ui": "TextInput",
		tone: rootTheme.tone,
		children: [
			prefixNode,
			t24,
			suffixNode
		]
	}), $[88] = prefixNode, $[89] = rootTheme.tone, $[90] = suffixNode, $[91] = t24, $[92] = t25) : t25 = $[92], t25;
}
function _temp3(v_0) {
	return v_0 === 0 || v_0 === 1 ? 0 : v_0 === 2 ? 1 : v_0 - 1;
}
function _temp2(v) {
	return v === 0 ? 0 : v === 1 || v === 2 ? 1 : v - 2;
}
function _temp(event) {
	event.preventDefault(), event.stopPropagation();
}
export { textInputRootStyle as a, useCustomValidity as c, focusFirstDescendant as d, focusLastDescendant as f, _raf2 as h, textInputRepresentationStyle as i, _hasFocus as l, _raf as m, textInputBaseStyle as n, responsiveInputPaddingIconRightStyle as o, isFocusable as p, textInputFontSizeStyle as r, responsiveInputPaddingStyle as s, TextInput as t, attemptFocus as u };

//# sourceMappingURL=textInput-VH0sQqrm.js.map